import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { Layout } from "./components/Layout";
import Home from "./pages/Home";
import Compliance from "./pages/Compliance";
import ComplianceFaq from "./pages/ComplianceFaq";
import TrustServicesFaq from "./pages/TrustServicesFaq";
import AmlAuditsFaq from "./pages/AmlAuditsFaq";
import CapitalMarketsFaq from "./pages/CapitalMarketsFaq";
import ExpertWitnessFaq from "./pages/ExpertWitnessFaq";
import ExpertWitnessIntake from "./pages/ExpertWitnessIntake";
import CapitalMarkets from "./pages/CapitalMarkets";
import TrustServices from "./pages/TrustServices";
import ExpertWitness from "./pages/ExpertWitness";
import WhoWeAre from "./pages/WhoWeAre";
import Contact from "./pages/Contact";
import AmlAudits from "./pages/AmlAudits";
import AdminLogin from "./pages/AdminLogin";
import AdminEnquiries from "./pages/AdminEnquiries";
import AuthCallback from "./pages/AuthCallback";
import { PrivacyPolicy, TermsConditions } from "./pages/Legal";
import { TrustServicesLanding, ComplianceLanding, AmlAuditsLanding, ExpertWitnessLanding, CapitalMarketsLanding } from "./pages/ServiceLanding";
import { installCalendlyBookingTracking } from "./lib/calendlyTracking";
import { trackPageView } from "./lib/ga";

const SEO_MAP = {
  "/": {
    title: "American Elm Advisors",
    description: "American Elm Advisors — Compliance Consulting, Capital Markets Consulting, and Trust Services Consulting for RIAs, broker-dealers, fund sponsors, and trust companies. Portsmouth, NH.",
  },
  "/compliance-consulting": {
    title: "Compliance Consulting for RIAs & Broker-Dealers | American Elm Advisors",
    description: "Proactive compliance programs for RIAs, broker-dealers, and insurance companies offering annuity products — SEC, FINRA, and state regulatory expertise.",
  },
  "/compliance-faq": {
    title: "Compliance Consulting FAQs | American Elm Advisors",
    description: "Answers about RIA and broker-dealer compliance consulting, outsourced CCO support, examination preparation, AML reviews and working with American Elm Advisors.",
  },
  "/trust-services-faq": {
    title: "Trust Services Consulting FAQs | American Elm Advisors",
    description: "Answers about trust company feasibility, charter readiness, governance, fiduciary compliance, examinations, operations and technology consulting from American Elm Advisors.",
  },
  "/aml-independent-audits-faq": {
    title: "AML Independent Audit FAQs | American Elm Advisors",
    description: "Answers about independent AML audits, audit scope, reviewer independence, testing, reports and remediation support for financial-services firms.",
  },
  "/capital-markets-faq": {
    title: "Capital Markets Consulting FAQs | American Elm Advisors",
    description: "Answers about fund structure, Rule 506 offerings, distribution strategy, diligence infrastructure and capital markets consulting for private-fund sponsors.",
  },
  "/expert-witness-faq": {
    title: "Expert Witness FAQs | American Elm Advisors",
    description: "Answers about securities and insurance expert witness consulting, independent analysis, reports, depositions and testimony for arbitration and legal proceedings.",
  },
  "/expert-witness-intake": {
    title: "Expert Witness Pre-Retention Intake | American Elm Advisors",
    description: "Confidential pre-retention intake for counsel evaluating an independent securities or insurance expert, including FINRA arbitration matters. Public information only; conflict check first.",
  },
  "/capital-markets-consulting": {
    title: "Capital Markets Consulting — Rule 506(b) & 506(c) Offerings | American Elm Advisors",
    description: "Distribution strategy, product structuring, and due-diligence infrastructure for private-fund sponsors raising capital under Rule 506(b) and Rule 506(c).",
  },
  "/trust-services-consulting": {
    title: "Trust Services Consulting — Charter Readiness & Fiduciary Compliance | American Elm Advisors",
    description: "Feasibility, charter readiness, governance, fiduciary compliance, examinations, operations, and technology consulting for trust companies and RIAs.",
  },
  "/expert-witness": {
    title: "Expert Witness Services — Jessica Murray | American Elm Advisors",
    description: "Expert witness consulting and testimony for arbitration and regulatory matters: broker-dealer and RIA supervision, suitability, fiduciary standards, annuities, and alternative investments.",
  },
  "/aml-independent-audits": {
    title: "AML Independent Audits for Broker-Dealers | American Elm Advisors",
    description: "Independent AML testing under FINRA Rule 3310, with a specialty in audits and evaluations for broker-dealers launching online platforms.",
  },
  "/who-we-are": {
    title: "Who We Are — Our Team | American Elm Advisors",
    description: "Meet the American Elm Advisors team — decades of seat-level experience across compliance, capital markets, trust services, and financial services operations.",
  },
  "/contact": {
    title: "Contact Us | American Elm Advisors — Portsmouth, NH",
    description: "Request a consultation or schedule a 15-minute call with American Elm Advisors — 104 Congress Street, Suite 404, Portsmouth, NH 03801.",
  },
  "/lp/trust-services": {
    title: "Trust Company Compliance & Fiduciary Support | American Elm Advisors",
    description: "Practical guidance for trust companies, RIAs and fiduciary businesses — regulatory support, governance, exam readiness, operations, and formation.",
  },
  "/lp/compliance-consulting": {
    title: "Compliance Consulting for Financial Services Firms | American Elm Advisors",
    description: "Practical compliance support for RIAs, broker-dealers, private funds and fiduciary businesses — program reviews, annual reviews, mock exams, AML, and outsourced projects.",
  },
  "/lp/aml-independent-audits": {
    title: "Independent AML Testing for Broker-Dealers | American Elm Advisors",
    description: "Independent AML testing under FINRA Rule 3310 for broker-dealers — including firms with online platforms and digital onboarding. Experienced outside testing from American Elm Advisors.",
  },
  "/lp/expert-witness": {
    title: "Securities & Insurance Expert Witness — FINRA Arbitration | American Elm Advisors",
    description: "Independent securities and insurance expert witness for FINRA arbitration and related matters — supervision, suitability, fiduciary standards, annuities, and alternative investments.",
  },
  "/lp/capital-markets": {
    title: "Capital Markets & Distribution Consulting for Private Funds | American Elm Advisors",
    description: "Distribution strategy, product structuring, and due-diligence infrastructure for sponsors raising under Rule 506(b) and 506(c). Strategy and diligence support — not a promise of capital.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | American Elm Advisors",
    description: "Privacy Policy for American Elm Advisors, LLC.",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | American Elm Advisors",
    description: "Terms and Conditions for the American Elm Advisors website.",
  },
};

let lastTrackedPath = null;

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const seo = SEO_MAP[pathname] || SEO_MAP["/"];
    document.title = seo.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", seo.description);
    if (lastTrackedPath === null) {
      lastTrackedPath = pathname;
    } else if (lastTrackedPath !== pathname) {
      trackPageView();
      lastTrackedPath = pathname;
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://americanelmadvisors.com${pathname}`);
    let robots = document.querySelector('meta[name="robots"]');
    if (pathname.startsWith("/lp/")) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }
    if (hash) {
      setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/compliance-consulting" element={<Compliance />} />
      <Route path="/compliance-faq" element={<ComplianceFaq />} />
      <Route path="/trust-services-faq" element={<TrustServicesFaq />} />
      <Route path="/aml-independent-audits-faq" element={<AmlAuditsFaq />} />
      <Route path="/capital-markets-faq" element={<CapitalMarketsFaq />} />
      <Route path="/expert-witness-faq" element={<ExpertWitnessFaq />} />
      <Route path="/expert-witness-intake" element={<ExpertWitnessIntake />} />
      <Route path="/capital-markets-consulting" element={<CapitalMarkets />} />
      <Route path="/trust-services-consulting" element={<TrustServices />} />
      <Route path="/expert-witness" element={<ExpertWitness />} />
      <Route path="/aml-independent-audits" element={<AmlAudits />} />
      <Route path="/who-we-are" element={<WhoWeAre />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/lp/trust-services" element={<TrustServicesLanding />} />
      <Route path="/lp/compliance-consulting" element={<ComplianceLanding />} />
      <Route path="/lp/aml-independent-audits" element={<AmlAuditsLanding />} />
      <Route path="/lp/expert-witness" element={<ExpertWitnessLanding />} />
      <Route path="/lp/capital-markets" element={<CapitalMarketsLanding />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/enquiries" element={<AdminEnquiries />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
};

function App() {
  useEffect(() => installCalendlyBookingTracking(), []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <Layout>
        <AppRoutes />
      </Layout>
      <Toaster position="bottom-right" />
    </BrowserRouter>
  );
}

export default App;
